import React, { useContext, useEffect, useState } from "react";
import { AttributeFilter } from "../../../common/interfaces";
import { IconInput } from "@glamcorner/components/lib/core/components/IconInput/IconInput";
import SearchIcon from "@glamcorner/components/lib/assets/icons/search-icn.inline.svg";
import { AttributeFilterOptionItem } from "../AttributeFilterItemCheckboxList/AttributeFilterOptionItem";
import "./attribute-filter-item-searchable-list.scss";
import { FilterContext } from "../../FilterContext";
import { compareAttributeFilterOptions } from "../../../common/util";
import Fuse from "fuse.js";

export interface AttributeFilterItemSearchableListProps {
  attributeFilter: AttributeFilter;
  placeholder: string;
}

const compareStrings = (s1: string, s2: string) => s1.toLowerCase().includes(s2.toLowerCase());

const fuzzySearch = (labels: string[], searchValue: string): string => {
  const labelInObjects = labels.map(d => ({ label: d }));
  const fuse = new Fuse(labelInObjects, {
    keys: ["label"],
    threshold: 0.6,
    distance: 100,
    maxPaternLength: 32,
    minMatchCharLength: 1
  });

  const labelObjectSelected = fuse.search(searchValue)[0] || {};
  return labelObjectSelected.label;
};

export const AttributeFilterItemSearchableList: React.FC<AttributeFilterItemSearchableListProps> = ({
  attributeFilter,
  placeholder
}) => {
  const { appliedAttributeFilters, applyAttributeFilter } = useContext(FilterContext);

  const [searchValue, setSearchValue] = useState("");
  const [isEmptyResult, setIsEmptyResult] = useState(false);
  const labels = attributeFilter.options.map(o => o.label);

  const getSuggestedLabel = (): string => {
    return fuzzySearch(labels, searchValue);
  };

  useEffect(() => {
    const allOptions = attributeFilter.options.map(o => o.label);
    setIsEmptyResult(!allOptions.some(label => compareStrings(label, searchValue)));
  }, [searchValue]);

  const attributeFilterOptions = () => {
    const appliedAttributeFilter = appliedAttributeFilters.find(filter => filter.code === attributeFilter.code);
    const currentAppliedOptions = (appliedAttributeFilter && appliedAttributeFilter.options) || [];

    const sortedAvailableOptions = [...attributeFilter.options].sort(compareAttributeFilterOptions);
    const optionsMatchingSearch = sortedAvailableOptions.filter(o => compareStrings(o.label, searchValue));

    return optionsMatchingSearch.map(option => {
      const isActive = !!currentAppliedOptions.find(opt => opt.label === option.label);
      const onSelected = () => {
        applyAttributeFilter({
          ...attributeFilter,
          options: isActive
            ? currentAppliedOptions.filter(opt => opt.label !== option.label)
            : [...currentAppliedOptions, option]
        });
      };
      return (
        <AttributeFilterOptionItem
          key={option.label}
          attributeFilterId={attributeFilter.code}
          attributeFilterOption={option}
          active={isActive}
          onSelected={onSelected}
        />
      );
    });
  };

  return (
    <div className="c-attribute-filter-item-searchable-list">
      <div className="c-attribute-filter-item-searchable-list__input">
        <IconInput
          placeholder={placeholder}
          icon={<SearchIcon />}
          onChange={(value: string) => setSearchValue(value)}
          value={searchValue}
        />
        {isEmptyResult && (
          <>
            <div className="c-attribute-filter-item-searchable-list__notice">Your search returned no results</div>
            <div className="c-attribute-filter-item-searchable-list__suggestion-container">
              Did you mean:{" "}
              <div
                className="c-attribute-filter-item-searchable-list__suggestion"
                data-testid="suggestion"
                onClick={() => setSearchValue(getSuggestedLabel())}
              >
                {getSuggestedLabel()}
              </div>
              ?
            </div>
          </>
        )}
      </div>
      <div className="c-attribute-filter-item-searchable-list__options">{attributeFilterOptions()}</div>
    </div>
  );
};
